// eslint-disable-next-line max-len
import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, Output, EventEmitter, ViewChild } from '@angular/core';
import { GetSellAreaCodeListService } from './api-services/get-sell-area-code-list.service';
import { SellAreaCodeComboBoxModel } from './sell-area-code-combo-box.model';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';
import { SellArea } from './sell-area.model';

/**
 * 共通部品 売り場コードコンボボックスを制御するクラス
 *
 * @export
 * @class SellAreaCodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 * @implements {ControlValueAccessor}
 */
@Component({
  selector: 'app-sell-area-code-combo-box',
  templateUrl: 'sell-area-code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SellAreaCodeComboBoxComponent,
      multi: true
    },
    {
      provide: GetSellAreaCodeListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetSellAreaCodeListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]

    }
  ],
})

export class SellAreaCodeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  public selectedIndex: number = <number>-1;
  public selectedSellAreaCode: string = <string>'';
  public selectedSellAreaName: string = <string>'';
  public selectedSellAreaCodeName: string = <string>'';
  public selectedUnityPrefectureDiv: string = <string>'';
  public selectedMediumType: string = <string>'';
  public model: SellAreaCodeComboBoxModel = new SellAreaCodeComboBoxModel();

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  @Input() initAutoLoad: boolean = <boolean>false;                              // オートロードフラグ
  @Input() mediumType: string = <string>'';                                     // 検索条件「媒体区分」を設定する(voの値)
  @Input() public tabindex: number = null;
  @Input() forcedEnabledMode: string = null;
  @Input() initFocusItem: any = null;                                           // ロード完了後フォーカスを移動させるid

  // レイアウト系の変数
  @Input() left: number = <number>0;
  @Input() top: number = <number>0;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() required: boolean = <boolean>false;
  @Input() afterFocus: any = null;                                              // ロード完了後フォーカスを移動させるid
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ
  @Output() loadCompleted: EventEmitter<any> = new EventEmitter<any>();         // ロード完了通知

  /**
   * Creates an instance of SellAreaCodeComboBoxComponent.
   * @memberof SellAreaCodeComboBoxComponent
   * @param {getSellAreaCodeListService} GetSellAreaCodeListService コードリストを取得する
   */
  constructor(
    public elementRef: ElementRef,
    private getSellAreaCodeListService: GetSellAreaCodeListService,
  ) { }

  // ValueAccessor
  public disabled: boolean = <boolean>false;
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedSellAreaCode) {
      this.selectedSellAreaCode = text;
      this.setSellAreaCode(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof SellAreaCodeComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof SellAreaCodeComboBoxComponent
   */
  ngAfterViewInit(): void {
    // 初期データ自動取得フラグのチェック
    if (this.model.initAutoLoad) {
      // データ取得
      this.getData();
    }
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes
   * @memberof SellAreaCodeComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof SellAreaCodeComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * クリア
   *
   * @memberof SellAreaCodeComboBoxComponent
   */
  public clear(): void {
    // コンボボックス情報初期化
    this.selectedMediumType = <string>'';
    this.selectedSellAreaCode = <string>'';
    this.selectedSellAreaName = <string>'';
    this.selectedSellAreaCodeName = <string>'';
    this.selectedUnityPrefectureDiv = <string>'';
    this.selectedIndex = <number>-1;
    this.model = new SellAreaCodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  setMediumType(mediumType: string, sellAreaCode = '', endFunc: () => void = null) {
    this.reload(mediumType, sellAreaCode, endFunc);
  }

  /**
   * コンボボックスの選択売り場コードNameを切り替える
   *
   * @param {string} sellAreaCode 売り場コード
   * @memberof SellAreaCodeComboBoxComponent
   */
  public setSellAreaCode(sellAreaCode: string): void {
    this.model.setSellAreaCode = sellAreaCode;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedSellAreaCode();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof SellAreaCodeComboBoxComponent
   */
  public updateSelectedSellAreaCode(): void {
    let tempList: SellArea[] = this.model.dataList;
    if (PlessViewUtilsSub.isEmptyArray(tempList)) {
      tempList = [];
    }

    for (let i = Number(0); i < tempList.length; i++) {
      if (tempList[i].SellAreaCode === this.model.setSellAreaCode) {
        this.selectedIndex = i;
      }
    }

    if (this.initFocusItem != null) {
      if (this.initFocusItem instanceof HTMLElement) {
        // 参照先の型がElementならそのままフォーカスをあてる
        this.initFocusItem.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.initFocusItem.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }

      this.initFocusItem = null;
    }

    // バインドフィールドを更新
    this.updateBindedField(null);
  }

  /**
   * コンボボックスの内容を再度ロードする
   *
   * @param {string} [mediumType=''] 媒体区分
   * @memberof SellAreaCodeComboBoxComponent
   */
  public reload(mediumType = '', sellAreaCode = '', endFunc: () => void = null): void {
    if (PlessViewUtilsSub.isEmpty(mediumType)) {
      this.clear();

      // コードリスト取得完了フラグを設定
      this.model.completeUpdateFlg = true;

      // 初期選択無しとする
      this.setSellAreaCode('');
    } else {
      // 媒体区分があればリロード
      this.getData(mediumType, endFunc);

      // 「20：中面」を初期選択とする
      this.setSellAreaCode(sellAreaCode);
    }
  }

  /**
   * バインドフィールド初期化
   *
   * @private
   * @param {*} e
   * @returns {void}
   * @memberof SellAreaCodeComboBoxComponent
   */
  public updateBindedField(e: any): void {
    try {
      if (e != null) {
        this.selectedIndex = e.target.selectedIndex;
      }
      if (this.selectedIndex === -1 ||
        this.model.dataList == null ||
        // 空行なしなら１行未満で初期化
        (!this.blank && this.model.dataList.length < 1) ||
        // 空行ありなら２行未満で初期化
        (this.blank && this.model.dataList.length < 2)) {
        this.selectedMediumType = <string>'';
        this.selectedSellAreaCode = <string>'';
        this.selectedSellAreaName = <string>'';
        this.selectedSellAreaCodeName = <string>'';
        this.selectedUnityPrefectureDiv = <string>'';
        return;
      }

      this.selectedMediumType = this.model.dataList[this.selectedIndex].MediumType;
      this.selectedSellAreaCode = this.model.dataList[this.selectedIndex].SellAreaCode;
      this.selectedSellAreaName = this.model.dataList[this.selectedIndex].SellAreaName;
      this.selectedSellAreaCodeName = this.model.dataList[this.selectedIndex].SellAreaCodeName;
      this.selectedUnityPrefectureDiv = this.model.dataList[this.selectedIndex].UnityPrefectureDiv;
    } finally {
      if (this.initFocusItem != null) {
        let queryedElement;
        if (this.initFocusItem instanceof HTMLElement) {
          queryedElement = this.initFocusItem.querySelector('textarea, input, select');
        } else {
          queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
        }
        (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
      }

      // ロード完了通知
      this.loadCompleted.emit(this.selectedSellAreaCode);
    }
  }

  /**
   * データ取得
   *
   * @memberof SellAreaCodeComboBoxComponent
   */
  public getData(mediumType: string = null, endFunc: () => void = null): void {
    this.getSellAreaCodeListService.execute(this.model, mediumType ? mediumType : this.mediumType, endFunc);
  }

}
